// ./src/components/features/EventSetup/setupStepRules.ts
//
// ROADMAP #161 b + d — the rules behind an event's getting-started steps, kept pure so `npm run check-event-setup-steps`
// RUNS them. Each step is DERIVED from what is true now (owner, 2026-10-15), never a stored "done" flag that drifts:
//
//   card payments   the event sells something paid online (the API's `eventPaidSales`), it is not in test mode, and
//                   Stripe has not answered that charges are enabled (`useCardPayments`).
//   default venue   a VENUE account (it hosts in its own space) with no default venue yet, on an event that has one:
//                   one tap saves it, so the next event starts there (`account_preference.default_venue_id`, #53).
//
// ⚠ The OWNER's steps only: Stripe and the account's defaults are the owner's, and crew cannot change either.
// ⓘ NO "this event has no venue" step (owner, 2026-10-16): the full wizard already requires a venue for an in-person
//   event, and the quick setups that skip it make events that need none (the place is on the picture or in the text).
import { EVENT_FORMAT } from '@/components/features/CreateEventWizard/wizardTypes';
import { VENUE_ACCOUNT_TYPE } from '@/config/accountTypes';

/** What an event sells online that costs money — the API's `PAID_SALE_KIND` (src/boundary/paidSales.ts). */
export const PAID_SALE_KIND = {
  TICKETS: 'tickets',
  SIGNUP_ITEMS: 'signup_items',
  SPONSORSHIPS: 'sponsorships',
} as const;

/** The account type that runs its own space — defined once, in `config/accountTypes`. */
export { VENUE_ACCOUNT_TYPE } from '@/config/accountTypes';

type EventLike = { eventFormat?: string | null; venueId?: string | null };

/** The event happens somewhere physical. */
export const isInPersonEvent = (event: EventLike | null | undefined): boolean =>
  event?.eventFormat === EVENT_FORMAT.IN_PERSON || event?.eventFormat === EVENT_FORMAT.HYBRID;

/**
 * #161 b — the card-payments step is due: something paid is sold online, outside test mode, and Stripe has not said
 * charges are enabled. ⚠ `paid === null` (not read, or the read failed) is NOT due — the step says nothing rather than
 * claim a sale it could not see; and before Stripe answers (`checked` false) nothing is claimed either way.
 */
export const cardPaymentsDue = (
  paid: { kinds?: readonly string[] | null; isTestMode?: boolean | null } | null,
  payments: { checked: boolean; status: { chargesEnabled?: boolean | null } | null },
): boolean => !!paid && (paid.kinds?.length ?? 0) > 0 && paid.isTestMode !== true && payments.checked && payments.status?.chargesEnabled !== true;

/** #184 — what the dashboard's test-mode banner offers. */
export const TEST_MODE_SWITCH = {
  /** Not in test mode, or cancelled (nothing is sold any more) — no banner. */
  NONE: 'none',
  /** In test mode, and this person may not change the event's settings: the banner says so, with no switch. */
  NOTICE: 'notice',
  /** The person's rights, or the owner's facts (what is sold, Stripe), are still being read: nothing claimed either way. */
  CHECKING: 'checking',
  /** Something paid is sold (or what is sold could not be read) and the owner's Stripe cannot take charges: connect first. */
  CONNECT_FIRST: 'connect_first',
  /** Something paid is sold and Stripe could not be asked: say so, and still offer the switch (unknown is not "cannot"). */
  UNCHECKED: 'unchecked',
  /** Offer "Switch to live payments". */
  READY: 'ready',
} as const;

export type TestModeSwitch = (typeof TEST_MODE_SWITCH)[keyof typeof TEST_MODE_SWITCH];

type PaidRead = { kinds?: readonly string[] | null } | null | undefined;

/** The paid things test mode covers — every one an event sells online (#188). */
const TEST_MODE_KINDS: readonly string[] = Object.values(PAID_SALE_KIND);

/**
 * #184 / #188 — what test mode covers: EVERY online payment of the event — tickets, sign-up items and sponsorships (owner,
 * 2026-10-21: "in test mode all the event related online payments should be in test mode"; sponsorships were charged for
 * real until then). Each one makes the switch to live need Stripe, and is called a test payment while testing. ⓘ Still a
 * filter: a kind the server adds later is not claimed until it is named here.
 */
export const testModeKinds = (kinds: readonly string[] | null | undefined): string[] => (kinds ?? []).filter((kind) => TEST_MODE_KINDS.includes(kind));

/**
 * #184 — the test-mode banner (owner, 2026-10-20: "show it on the dashboard at the top and let user in one click enabled
 * real mode"). A live event that takes payments online needs the owner's Stripe to take charges, so the owner is
 * sent to connect it first rather than switched into a mode where no buyer can pay (ROADMAP #184).
 * ⚠ `paid`: undefined = not read yet, null = the read FAILED (not "sells nothing" — it is treated as maybe-paid), else
 *   the answer. ⚠ Crew (`owns` false) cannot see the owner's Stripe — they get the switch, and the confirm says buyers
 *   can pay only once the owner's card payments are on.
 */
export const testModeSwitch = (facts: {
  isTestMode: boolean | null | undefined;
  canceled: boolean;
  /** The crew access is still being read — `mayEditSettings` false then means "not known yet", not "may not". */
  rightsLoading: boolean;
  mayEditSettings: boolean;
  owns: boolean;
  paid: PaidRead;
  payments: { checked: boolean; status: { chargesEnabled?: boolean | null } | null };
}): TestModeSwitch => {
  if (facts.isTestMode !== true || facts.canceled) {
    return TEST_MODE_SWITCH.NONE;
  }

  if (facts.rightsLoading) {
    return TEST_MODE_SWITCH.CHECKING;
  }

  if (!facts.mayEditSettings) {
    return TEST_MODE_SWITCH.NOTICE;
  }

  if (!facts.owns) {
    return TEST_MODE_SWITCH.READY;
  }

  if (facts.paid && testModeKinds(facts.paid.kinds).length === 0) {
    return TEST_MODE_SWITCH.READY;
  }

  if (facts.payments.status?.chargesEnabled === true) {
    return TEST_MODE_SWITCH.READY;
  }

  if (facts.paid === undefined || !facts.payments.checked) {
    return TEST_MODE_SWITCH.CHECKING;
  }

  if (!facts.payments.status) {
    return TEST_MODE_SWITCH.UNCHECKED;
  }

  return TEST_MODE_SWITCH.CONNECT_FIRST;
};

/** #184 — the owner's Stripe is asked only when its answer can change the banner: something paid is, or may be, sold. */
export const testModeNeedsStripe = (facts: { isTestMode: boolean | null | undefined; canceled: boolean; mayEditSettings: boolean; owns: boolean; paid: PaidRead }): boolean =>
  facts.isTestMode === true && !facts.canceled && facts.mayEditSettings && facts.owns && facts.paid !== undefined && (facts.paid === null || testModeKinds(facts.paid.kinds).length > 0);

/**
 * #161 d — offer "make this my venue": a venue account, preferences READ and holding no default, and an in-person event
 * that has a venue. ⚠ `preferenceLoaded` false is not "no default" — offering then would overwrite one the host saved.
 */
export const offerDefaultVenue = (facts: {
  accountType: string | null | undefined;
  preferenceLoaded: boolean;
  defaultVenueId: string | null | undefined;
  event: EventLike | null | undefined;
  dismissed: boolean;
}): boolean =>
  facts.accountType === VENUE_ACCOUNT_TYPE &&
  facts.preferenceLoaded &&
  !facts.defaultVenueId &&
  isInPersonEvent(facts.event) &&
  !!facts.event?.venueId &&
  !facts.dismissed;

/** The order the kinds are named in, whatever order they arrive in. */
const KIND_ORDER: readonly string[] = [PAID_SALE_KIND.TICKETS, PAID_SALE_KIND.SIGNUP_ITEMS, PAID_SALE_KIND.SPONSORSHIPS];

/**
 * What is sold, as words the host reads: "tickets", "tickets and sign-up items". `names` maps each kind to its word (the
 * caller translates); a kind this build does not know is left out rather than printed as a code.
 */
export const paidSalesPhrase = (kinds: readonly string[], names: Record<string, string>, locale?: string): string => {
  const words = KIND_ORDER.filter((kind) => kinds.includes(kind) && names[kind]).map((kind) => names[kind]);

  if (words.length <= 1) {
    return words[0] ?? '';
  }

  try {
    return new Intl.ListFormat(locale || undefined, { style: 'long', type: 'conjunction' }).format(words);
  } catch {
    return words.join(', ');
  }
};

/** #188 — the counts of `eventTestRecords` this rule reads. */
type TestRecordCounts = { guestCount?: number | null; orderCount?: number | null; signupOrderCount?: number | null; applicationCount?: number | null; ticketCount?: number | null; paymentCount?: number | null };

/**
 * #188 — the records a host thinks of as "made by the test run" (tickets and payments belong to the orders) — or, when only
 * tickets / payments are left (their guests or orders cleared on their own), those: never 0 while anything is there, or
 * Clear would be offered nothing to clear.
 */
export const testRecordTotal = (r: TestRecordCounts | null | undefined): number => {
  if (!r) {
    return 0;
  }

  const roots = (r.guestCount || 0) + (r.orderCount || 0) + (r.signupOrderCount || 0) + (r.applicationCount || 0);

  return roots > 0 ? roots : (r.ticketCount || 0) + (r.paymentCount || 0);
};

/** #198 c — what the "try a test purchase" step offers. */
export const TEST_PURCHASE_STEP = {
  /** Not due: nothing sold online, already selling for real, a test record exists, not the owner's, dismissed — or unknown. */
  NONE: 'none',
  /** Test mode is off on a draft: offer to turn it on. */
  TURN_ON: 'turn_on',
  /** Test mode is on and nothing was bought yet: say how to buy with a test card. */
  BUY: 'buy',
} as const;

export type TestPurchaseStep = (typeof TEST_PURCHASE_STEP)[keyof typeof TEST_PURCHASE_STEP];

/**
 * #198 c (owner, 2026-10-21) — "a test purchase in test mode before the first sale": on the owner's event that sells
 * something online (tickets, sign-up items, sponsorships) and has not started selling for real — a DRAFT, or an event in
 * TEST MODE — until a test record exists. ⚠ Unknown is not due: `paid` / `testRecords` null (not read, or the read
 * failed) say nothing — a nudge must not claim a sale or a test it could not see.
 * ⓘ A live, published event out of test mode is already selling: too late to rehearse, never nagged.
 */
export const testPurchaseStep = (facts: {
  owns: boolean;
  eventStatus: string | null | undefined;
  isTestMode: boolean | null | undefined;
  paid: { kinds?: readonly string[] | null } | null;
  testRecords: number | null;
  dismissed: boolean;
}): TestPurchaseStep => {
  const preLaunch = facts.eventStatus === 'draft' || (facts.isTestMode === true && facts.eventStatus === 'published');

  if (!facts.owns || facts.dismissed || !preLaunch || !facts.paid || testModeKinds(facts.paid.kinds).length === 0 || facts.testRecords !== 0) {
    return TEST_PURCHASE_STEP.NONE;
  }

  return facts.isTestMode === true ? TEST_PURCHASE_STEP.BUY : TEST_PURCHASE_STEP.TURN_ON;
};
